import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Check, ChevronDown } from 'lucide-react';
import { useCurrency, CURRENCIES } from '../context/CurrencyContext';

const CurrencyPicker = () => {
  const { currency, changeCurrency } = useCurrency();
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const inputRef          = useRef(null);
  const modalRef          = useRef(null);

  const filtered = CURRENCIES.filter(c =>
    c.name.toLowerCase().includes(query.toLowerCase()) ||
    c.code.toLowerCase().includes(query.toLowerCase()) ||
    c.country.toLowerCase().includes(query.toLowerCase())
  );

  /* auto-focus search on open */
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
    else setQuery('');
  }, [open]);

  /* close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  /* close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const select = (c) => {
    changeCurrency(c);
    setOpen(false);
  };

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border transition-all select-none"
        style={{
          background: 'rgba(196,69,105,0.07)',
          borderColor: '#f9b8ca',
          color: '#c44569',
        }}
        onMouseEnter={e => { e.currentTarget.style.background='rgba(196,69,105,0.14)'; }}
        onMouseLeave={e => { e.currentTarget.style.background='rgba(196,69,105,0.07)'; }}
        title="Change Currency"
      >
        <span style={{fontSize:'1.1rem',lineHeight:1}}>{currency.flag}</span>
        <span>{currency.code}</span>
        <ChevronDown size={13} strokeWidth={2.5} />
      </button>

      {/* Modal overlay */}
      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(30,10,20,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'fadeIn 0.18s ease',
          }}
        >
          <div
            ref={modalRef}
            style={{
              background: '#fff',
              borderRadius: '1.25rem',
              width: '100%',
              maxWidth: '440px',
              maxHeight: '82vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 24px 60px rgba(196,69,105,0.22), 0 4px 20px rgba(0,0,0,0.12)',
              animation: 'slideUp 0.22s cubic-bezier(0.34,1.56,0.64,1)',
              margin: '0 16px',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #fce0e8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #c44569, #cf6a87)',
            }}>
              <div>
                <h3 style={{color:'#fff',fontWeight:700,fontSize:'1rem',margin:0}}>Select Currency</h3>
                <p style={{color:'rgba(255,255,255,0.80)',fontSize:'0.75rem',margin:0}}>
                  Currently: {currency.flag} {currency.name} ({currency.code})
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background:'rgba(255,255,255,0.18)',border:'none',
                  borderRadius:'8px',padding:'6px',cursor:'pointer',
                  display:'flex',alignItems:'center',color:'#fff',
                }}
              >
                <X size={16} strokeWidth={2.5} />
              </button>
            </div>

            {/* Search */}
            <div style={{padding:'12px 16px',borderBottom:'1px solid #fce0e8'}}>
              <div style={{position:'relative'}}>
                <Search size={15} style={{position:'absolute',left:'10px',top:'50%',transform:'translateY(-50%)',color:'#cf6a87'}} />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search country, currency name or code..."
                  style={{
                    width: '100%',
                    paddingLeft: '32px',
                    paddingRight: '12px',
                    paddingTop: '9px',
                    paddingBottom: '9px',
                    borderRadius: '10px',
                    border: '1.5px solid #f9b8ca',
                    fontSize: '0.85rem',
                    outline: 'none',
                    background: '#fdf0f4',
                    color: '#333',
                    boxSizing: 'border-box',
                  }}
                  onFocus={e => { e.target.style.borderColor='#c44569'; e.target.style.boxShadow='0 0 0 3px rgba(196,69,105,0.15)'; }}
                  onBlur={e  => { e.target.style.borderColor='#f9b8ca'; e.target.style.boxShadow='none'; }}
                />
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    style={{position:'absolute',right:'8px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#cf6a87',padding:'2px'}}
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
              <p style={{fontSize:'0.72rem',color:'#cf6a87',margin:'6px 0 0 2px'}}>
                {filtered.length} {filtered.length === 1 ? 'currency' : 'currencies'} found
              </p>
            </div>

            {/* List */}
            <div style={{overflowY:'auto',flex:1}}>
              {filtered.length === 0 ? (
                <div style={{padding:'32px',textAlign:'center',color:'#cf6a87',fontSize:'0.85rem'}}>
                  No currencies found for "{query}"
                </div>
              ) : (
                filtered.map(c => {
                  const isActive = c.code === currency.code;
                  return (
                    <button
                      key={c.code}
                      onClick={() => select(c)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '11px 16px',
                        background: isActive ? '#fdf0f4' : 'transparent',
                        border: 'none',
                        borderBottom: '1px solid #fef3f7',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background='#fef6f8'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background='transparent'; }}
                    >
                      {/* Flag */}
                      <span style={{fontSize:'1.5rem',lineHeight:1,flexShrink:0,width:'32px',textAlign:'center'}}>
                        {c.flag}
                      </span>
                      {/* Info */}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          color: isActive ? '#c44569' : '#1e293b',
                          marginBottom:'1px',
                        }}>
                          {c.country}
                        </div>
                        <div style={{fontSize:'0.75rem',color:'#94a3b8'}}>
                          {c.name}
                        </div>
                      </div>
                      {/* Code + Symbol */}
                      <div style={{textAlign:'right',flexShrink:0}}>
                        <div style={{fontWeight:700,fontSize:'0.82rem',color: isActive ? '#c44569' : '#475569'}}>
                          {c.code}
                        </div>
                        <div style={{fontSize:'0.75rem',color:'#94a3b8'}}>{c.symbol}</div>
                      </div>
                      {/* Check */}
                      {isActive && (
                        <Check size={15} strokeWidth={2.5} style={{color:'#c44569',flexShrink:0}} />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Keyframe animations */}
      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { opacity:0; transform:translateY(24px) scale(0.97) } to { opacity:1; transform:translateY(0) scale(1) } }
      `}</style>
    </>
  );
};

export default CurrencyPicker;
